import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { canvaService } from '@/services/canva.service';
import { CanvaImportJobStatus } from '@/types/canva';
import { usePostsStore } from './postsStore';
import { toast } from '@/utils/toast';
import { devLog } from '@/utils/logger';

/**
 * Canva Import Store
 *
 * Manages background Canva import jobs that continue when the modal is closed.
 * Shows a minimized progress indicator in the bottom-left corner.
 */

interface CanvaImportState {
  // Active background import
  activeImport: {
    jobId: number;
    status: CanvaImportJobStatus | null;
    requestReopen: boolean;
  } | null;

  // Actions
  startBackgroundImport: (jobId: number) => void;
  clearBackgroundImport: () => void;
  updateStatus: (status: CanvaImportJobStatus) => void;
  reopenModal: () => void;
}

// Polling interval reference
let pollInterval: ReturnType<typeof setInterval> | null = null;

export const useCanvaImportStore = create<CanvaImportState>()(
  devtools(
    (set, get) => ({
      activeImport: null,

      /**
       * Start tracking an import in the background
       * Called when user closes modal during active import
       */
      startBackgroundImport: (jobId: number) => {
        // Clear any existing poll
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }

        set({ activeImport: { jobId, status: null, requestReopen: false } });

        // Start polling
        const poll = async () => {
          try {
            const status = await canvaService.getImportStatus(jobId);
            set((state) => ({
              activeImport: state.activeImport
                ? { ...state.activeImport, status }
                : null,
            }));

            // Check if complete
            if (
              status.status === 'COMPLETED' ||
              status.status === 'FAILED' ||
              status.status === 'CANCELLED'
            ) {
              // Stop polling
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }

              // Show toast notification
              if (status.status === 'COMPLETED') {
                toast.success(
                  `Canva import complete: ${status.successCount}/${status.totalDesigns} designs imported`
                );
                // Refresh posts list
                usePostsStore.getState().fetchPosts();
                usePostsStore.getState().fetchStats();
              } else if (status.status === 'FAILED') {
                toast.error('Canva import failed');
              } else {
                toast.error('Canva import was cancelled');
              }

              // Clear after a delay to show final status
              setTimeout(() => {
                set({ activeImport: null });
              }, 3000);
            }
          } catch (error) {
            devLog.error('Failed to poll Canva import status:', error);
          }
        };

        // Initial poll
        poll();

        // Continue polling every 2 seconds
        pollInterval = setInterval(poll, 2000);
      },

      /**
       * Clear background import (user dismissed it)
       */
      clearBackgroundImport: () => {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        set({ activeImport: null });
      },

      /**
       * Update status (used when modal is still open)
       */
      updateStatus: (status: CanvaImportJobStatus) => {
        set((state) => ({
          activeImport: state.activeImport
            ? { ...state.activeImport, status }
            : null,
        }));
      },

      /**
       * Reopen the modal (called when user clicks minimized indicator)
       */
      reopenModal: () => {
        const { activeImport } = get();
        if (activeImport) {
          // Stop background polling - modal will take over
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }

          // Set the reopen flag (don't clear activeImport - parent needs jobId)
          set({
            activeImport: {
              ...activeImport,
              requestReopen: true,
            },
          });
        }
      },

    }),
    {
      name: 'CanvaImportStore',
      enabled: import.meta.env.DEV,
    }
  )
);
